import { ReviewItem } from 'src/apollo/onlineOrdering'
import { Review } from 'src/apollo/sites'
import { parseSlateText } from '@toasttab/sites-components'
import { t } from 'src/translations'

export const REVIEW_SOURCE = {
  MANUAL: null,
  GOOGLE: 'google'
} as const

export {
  PLACEHOLDER_REVIEW_CONTENT,
  isPlaceholderReview
} from '@toasttab/sites-components'

export type NormalizedReview = Review & {
  updateTime?: string | null
}

const starRatingToNumber = (rating?: string | null): number => {
  if (!rating) return 0

  const ratingMap: { [key: string]: number } = {
    FIVE: 5,
    FOUR: 4,
    THREE: 3,
    TWO: 2,
    ONE: 1
  }
  return ratingMap[rating] || 0
}

export const mapGoogleReviewToNormalized = (
  review: ReviewItem | null
): NormalizedReview | null => {
  if (!review) return null

  return {
    __typename: 'Review',
    id: review.reviewId,
    authorName: review.reviewer?.displayName || t('reviews.anonymous'),
    authorImg: review.reviewer?.profilePhotoUrl || null,
    content: review.comment || '',
    rating: starRatingToNumber(review.starRating),
    source: REVIEW_SOURCE.GOOGLE,
    updateTime: review.updateTime || review.createTime
  }
}

export const mapManualReviewToNormalized = (
  review: Review
): NormalizedReview => {
  return {
    ...review,
    authorName: review.authorName
      ? parseSlateText(review.authorName)
      : review.authorName,
    content: parseSlateText(review.content || '')
  }
}

export const getRelativeTime = (dateString?: string | null): string => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return t('reviews.time.today')
  if (diffInDays === 1) return t('reviews.time.day-ago', { count: 1 })
  if (diffInDays < 7) return t('reviews.time.days-ago', { count: diffInDays })
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7)
    return weeks === 1
      ? t('reviews.time.week-ago', { count: 1 })
      : t('reviews.time.weeks-ago', { count: weeks })
  }
  if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30)
    return months === 1
      ? t('reviews.time.month-ago', { count: 1 })
      : t('reviews.time.months-ago', { count: months })
  }
  const years = Math.floor(diffInDays / 365)
  return years === 1
    ? t('reviews.time.year-ago', { count: 1 })
    : t('reviews.time.years-ago', { count: years })
}

/**
 * Returns high-rated Google reviews (4+ stars) with comments.
 * Filters for reviews with 4 or 5 star ratings and non-empty comments.
 * Limited to the first 6 matching reviews.
 *
 * @param reviews - Array of Google ReviewItems
 * @returns Array of up to 6 NormalizedReviews with 4+ star ratings
 */
export const getHighRatedGoogleReviews = (
  reviews: Array<ReviewItem | null>
): NormalizedReview[] => {
  return reviews
    .filter((review): review is ReviewItem => {
      if (!review) return false
      const rating = starRatingToNumber(review.starRating)
      return rating >= 4 && !!review.comment && review.comment.trim() !== ''
    })
    .slice(0, 6)
    .map(mapGoogleReviewToNormalized)
    .filter((review): review is NormalizedReview => review !== null)
}
